/* PLW-029: "Build your order" grid (sections/plw-variant-grid.liquid).
   Adds several variants of one product in a single /cart/add.js request, then
   re-renders the cart drawer the same way assets/product-form.js does. */
if (!customElements.get('plw-variant-grid')) {
  customElements.define(
    'plw-variant-grid',
    class PlwVariantGrid extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.inputs = Array.from(this.querySelectorAll('.plw-variant-grid__input'));
        this.submitButton = this.querySelector('.plw-variant-grid__submit');
        this.submitText = this.querySelector('.plw-variant-grid__submit-text');
        this.spinner = this.querySelector('.loading__spinner');
        this.countEl = this.querySelector('.plw-variant-grid__count');
        this.totalEl = this.querySelector('.plw-variant-grid__total');
        this.errorWrapper = this.querySelector('.plw-variant-grid__error');
        this.errorText = this.querySelector('.plw-variant-grid__error-text');
        this.statusEl = this.querySelector('.plw-variant-grid__status');
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        const onChange = () => {
          this.sanitize();
          this.updateSummary();
          this.showError();
        };
        this.addEventListener('input', onChange);
        this.addEventListener('change', onChange);
        this.inputs.forEach((input) => {
          input.addEventListener('focus', () => input.select());
          input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              input.blur();
            }
          });
        });
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        this.updateSummary();
      }

      sanitize() {
        this.inputs.forEach((input) => {
          if (input.value === '') return;
          const value = parseInt(input.value, 10);
          if (isNaN(value) || value < 0) input.value = 0;
          else if (String(value) !== input.value) input.value = value;
        });
      }

      getItems() {
        return this.inputs
          .map((input) => ({ id: parseInt(input.dataset.variantId, 10), quantity: parseInt(input.value, 10) || 0 }))
          .filter((item) => item.quantity > 0);
      }

      updateSummary() {
        let count = 0;
        let total = 0;
        this.inputs.forEach((input) => {
          const qty = parseInt(input.value, 10) || 0;
          if (qty > 0) {
            count += qty;
            total += qty * (parseInt(input.dataset.price, 10) || 0);
          }
        });
        this.countEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
        this.totalEl.textContent = this.formatMoney(total);
        if (this.loading === true) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
          const fixed = (number / 100).toFixed(precision);
          const parts = fixed.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? dollars + decimal + parts[1] : dollars;
        };
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return withDelimiters(cents, 2);
        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = withDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(cents, 2, "'", '.');
            break;
          default:
            value = withDelimiters(cents, 2);
        }
        return format.replace(/\{\{\s*\w+\s*\}\}/, value);
      }

      showError(message) {
        if (!this.errorWrapper) return;
        this.errorWrapper.hidden = !message;
        this.errorText.textContent = message || '';
      }

      setLoading(loading) {
        this.loading = loading;
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
        this.updateSummary();
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.loading) return;
        const items = this.getItems();
        if (!items.length) {
          this.showError('Enter a quantity for at least one option.');
          return;
        }
        this.showError();
        this.setLoading(true);

        const cart = document.querySelector('cart-drawer');
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement || this.submitButton);
        }

        const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
        const quantityAdded = items.reduce((sum, item) => sum + item.quantity, 0);

        fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
          .then(({ ok, data }) => {
            if (!ok || data.status) {
              const message =
                data.description || data.message || (window.cartStrings && window.cartStrings.error) || 'Could not add to cart.';
              this.showError(message);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'plw-variant-grid',
                  errors: data.errors || data.description,
                  message: data.message,
                });
              }
              return;
            }

            this.inputs.forEach((input) => {
              input.value = 0;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            });
            if (this.statusEl) {
              this.statusEl.textContent = `Added ${quantityAdded} ${quantityAdded === 1 ? 'item' : 'items'} to cart.`;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'plw-variant-grid', cartData: data });
            }

            if (cart && data.sections && typeof cart.renderContents === 'function') {
              cart.classList.remove('is-empty');
              cart.renderContents(data);
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch((error) => {
            console.error(error);
            this.showError((window.cartStrings && window.cartStrings.error) || 'Could not add to cart. Please try again.');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }
    }
  );
}
